import { Injectable } from '@angular/core';
import { PasswordValidationRule } from '../models/form.model';

@Injectable({
  providedIn: 'root',
})
export class FormValidationService {
  constructor() { }

  validateEmail(email: string): { valid: boolean; message: string } {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const doubleSymbolRegex = /(\.\.|@@|--|\+\+|__|\.@|@\.|-@|@-|_\.|@\+|\+@)/;

    if (!email.trim()) {
      return { valid: false, message: 'Email must be filled in' };
    } else if (email.length < 10) {
      return { valid: false, message: 'Minimum 10 characters' };
    } else if (email.length > 50) {
      return { valid: false, message: 'Maximum 50 characters' };
    } else if (!regex.test(email)) {
      return { valid: false, message: 'Email must be a valid email address' };
    } else if (doubleSymbolRegex.test(email)) {
      return { valid: false, message: 'Email contains invalid consecutive symbols' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validatePhoneNumber(phoneNumber: string): { valid: boolean; message: string } {
    const regex = /^\d+$/;

    if (!phoneNumber.trim()) {
      return { valid: false, message: 'Phone number must be filled in' };
    } else if (!regex.test(phoneNumber)) {
      return { valid: false, message: 'Phone number must be a number' };
    } else if (!phoneNumber.startsWith('8')) {
      return { valid: false, message: 'Phone number must start with number 8' };
    } else if (phoneNumber.length < 9) {
      return { valid: false, message: 'Minimum 9 digits' };
    } else if (phoneNumber.length > 12) {
      return { valid: false, message: 'Maximum 12 digits' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validatePassword(value: string, validationRules: PasswordValidationRule[]) {
    const min8 = /.{8,}/;
    const minCapital = /(?=.*[A-Z])/;
    const minText = /(?=.*[a-z])/;
    const minNum = /(?=.*[0-9])/;
    const minChar = /[`~!()\-_+={}\[\]:;'.<>\/]/;
    const disallowedChar = /[^a-zA-Z0-9`~!()\-_+={}\[\]:;'.<>\/]/;
    const maxChar = /^.{1,32}$/;

    let allRulesValid = true;

    validationRules.forEach((rule) => {
      switch (rule.key) {
        case 'min8':
          rule.status = min8.test(value);
          break;
        case 'minCapital':
          rule.status = minCapital.test(value);
          break;
        case 'minText':
          rule.status = minText.test(value);
          break;
        case 'minNum':
          rule.status = minNum.test(value);
          break;
        case 'minChar':
          rule.status = minChar.test(value) && !disallowedChar.test(value);
          break;
        case 'maxChar':
          rule.status = maxChar.test(value);
          break;
      }

      if (!rule.status) {
        allRulesValid = false;
      }
    });

    return {
      isValid: allRulesValid,
      errors: validationRules.filter((rule) => !rule.status).map((rule) => rule.error),
    };
  }

  validatePin(value: string): { valid: boolean; message: string } {
    if (!value.trim()) {
      return { valid: false, message: 'Pin must be filled in' };
    } else if (!/^\d+$/.test(value)) {
      return { valid: false, message: 'Pin must be a number' };
    } else if (value.length < 6) {
      return { valid: false, message: 'Minimum 6 digits' };
    } else if (value.length > 6) {
      return { valid: false, message: 'Maximum 6 digits' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validateConfirmPin(value: string, pin: string): { valid: boolean; message: string } {
    if (!value.trim()) {
      return { valid: false, message: 'Confirm Pin must be filled in' };
    } else if (!/^\d+$/.test(value)) {
      return { valid: false, message: 'Confirm Pin must be a number' };
    } else if (value !== pin) {
      return { valid: false, message: 'Make sure the PIN confirmation is the same as the previous PIN.' };
    } else if (value.length < 6) {
      return { valid: false, message: 'Minimum 6 digits' };
    } else if (value.length > 6) {
      return { valid: false, message: 'Maximum 6 digits' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validatePasswordInput(event: KeyboardEvent): boolean {
    const regex = /^[a-zA-Z0-9`~!()\-_+={}\[\]:;'.<>\/]+$/g;
    return regex.test(event.key);
  }

  validateUrl(value: string): { valid: boolean; message: string } {
    const regex = /^[a-zA-Z0-9-:.\\/]+$/;
    const invalidSequenceRegex = /\.\.+/;
    const invalidHyphenSequenceRegex = /--+/;

    if (!value || value.length === 0) {
      return { valid: true, message: '' };
    } else if (value.length < 7) {
      return { valid: false, message: 'URL must be at least 7 characters long' };
    } else if (!regex.test(value)) {
      return { valid: false, message: 'Contains not allowed character' };
    } else if (invalidSequenceRegex.test(value) || invalidHyphenSequenceRegex.test(value)) {
      return { valid: false, message: 'Format not supported' };
    } else if (!value.startsWith('https://') && !value.startsWith('http://')) {
      return { valid: false, message: 'URL must start with https:// or http://' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validateIPAddress(value: string): { valid: boolean; message: string } {
    const regex = /^[a-zA-Z0-9._\-\/]+$/;
    const alphanumericRegex = /[a-zA-Z0-9]/g;
    const invalidSequenceRegex = /\.\.+/;
    const alphanumericLength = (value.match(alphanumericRegex) || []).length;

    if (!value || value.length === 0) {
      return { valid: false, message: 'IP Address is required' };
    } else if (alphanumericLength < 5) {
      return { valid: false, message: 'IP Address must be at least 5 characters long' };
    } else if (!regex.test(value)) {
      return { valid: false, message: 'Contains not allowed character' };
    } else if (invalidSequenceRegex.test(value)) {
      return { valid: false, message: 'Format not supported' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validateKeyCertificate(value: string): { valid: boolean; message: string } {
    const regex = /^[a-zA-Z0-9:]+$/;
    const alphanumericLength = (value.match(/[a-zA-Z0-9]/g) || []).length;

    if (!value || value.length === 0) {
      return { valid: false, message: 'Key Certificate is required' };
    } else if (alphanumericLength < 40) {
      return { valid: false, message: 'Key must contain at least 40 alphanumeric characters' };
    } else if (!regex.test(value)) {
      return { valid: false, message: 'Contains not allowed character' };
    } else {
      return { valid: true, message: '' };
    }
  }

  validatePackageName(value: string): { valid: boolean; message: string } {
    const regex = /^[a-zA-Z0-9:._\-\/]+$/;
    const alphanumericLength = (value.match(/[a-zA-Z0-9]/g) || []).length;

    if (!value || value.length === 0) {
      return { valid: false, message: 'Package Name is required' };
    } else if (alphanumericLength < 2) {
      return { valid: false, message: 'Package Name must contain at least 2 characters' };
    } else if (!regex.test(value)) {
      return { valid: false, message: 'Contains not allowed character' };
    } else {
      return { valid: true, message: '' };
    }
  }

  allowOnlyAlphaNumericSpecial(event: KeyboardEvent): boolean {
    const regex = /^[a-zA-Z0-9._\-\/]+$/;
    return regex.test(event.key);
  }
}
